import React, { useEffect, useMemo, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import RestService from '../Services/RestService';
import OKomBreadcrumb from '../Components/OsloKommuneUI/OKomBreadcrumb';
import OKomLoading from '../Components/OsloKommuneUI/OKomLoading';
import OKomCheckbox from '../Components/OsloKommuneUI/OKomCheckbox';
import OKomModal from '../Components/OsloKommuneUI/OKomModal';
import AuthImage from '../Components/AuthImage';
import { useUserContext } from '../Components/StateContext/UserContext';
import myconfig from '../Config';

const eventKey = (p: Api.PhotoDto) => `${p.EventType}-${p.Year}`;

// "12.03.2026 14:05" — date + time in Norwegian locale; empty for missing/invalid values.
const formatDate = (value?: string): string => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('nb-NO') + ' ' + d.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
};

// Human-readable file size, e.g. "2.0 MB".
const formatBytes = (bytes?: number): string => {
  if (!bytes || bytes <= 0) return 'Ukjent';
  const units = ['B', 'KB', 'MB', 'GB'];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const thumbPlaceholder = (
  <div style={{
    width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#f2f2f2', color: '#888', fontSize: '0.8rem', textAlign: 'center', padding: 4,
  }}>
    Behandles…
  </div>
);

const BilderView: React.FC<RouteComponentProps> = () => {
  const [photos, setPhotos] = useState<Api.PhotoDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [lightbox, setLightbox] = useState<Api.PhotoDto | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { userRoles } = useUserContext();
  const isAdmin = userRoles.includes('admin');

  useEffect(() => { document.title = 'Bilder'; }, []);

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const data = await RestService.get<Api.PhotoDto[]>('/api/photos/List');
        setPhotos(data || []);
      } catch (e) {
        toast.error(`Kunne ikke hente bilder: ${e instanceof Error ? e.message : e}`);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  // Distinct event/year filters, derived from the visible photos (newest year first).
  const events = useMemo(() => {
    const map = new Map<string, { key: string; label: string; count: number; year: number; eventType: string }>();
    photos.forEach(p => {
      const key = eventKey(p);
      const e = map.get(key) || { key, label: p.EventLabel, count: 0, year: p.Year, eventType: p.EventType };
      e.count++;
      map.set(key, e);
    });
    return Array.from(map.values()).sort((a, b) => b.year - a.year || a.eventType.localeCompare(b.eventType));
  }, [photos]);

  // No event checked = show everything; otherwise show the union of the checked events.
  const filtered = useMemo(
    () => (selectedKeys.length === 0 ? photos : photos.filter(p => selectedKeys.includes(eventKey(p)))),
    [photos, selectedKeys]
  );

  const toggleKey = (key: string, checked: boolean) =>
    setSelectedKeys(prev => (checked ? [...prev, key] : prev.filter(k => k !== key)));

  // While the modal is open, ←/→ step to the previous/next photo in the current filtered set.
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const idx = filtered.findIndex(p => p.Id === lightbox.Id);
      if (idx === -1) return;
      const nextIdx = e.key === 'ArrowRight' ? idx + 1 : idx - 1;
      if (nextIdx >= 0 && nextIdx < filtered.length) {
        e.preventDefault();
        setLightbox(filtered[nextIdx]);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, filtered]);

  // Position of the open photo within the filtered set (-1 if none) — drives the prev/next arrows.
  const currentIndex = lightbox ? filtered.findIndex(p => p.Id === lightbox.Id) : -1;

  const downloadRaw = async (photo: Api.PhotoDto) => {
    try {
      setDownloading(true);
      const token = localStorage.getItem('myToken');
      const res = await fetch(`${myconfig.rootUrl}/api/photos/Image/${photo.Id}/raw`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) { toast.error('Kunne ikke laste ned bildet.'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = photo.OriginalFileName || `bilde-${photo.Id}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Nedlasting feilet.');
    } finally {
      setDownloading(false);
    }
  };

  // Soft-delete (admin only). Removes the photo from the list and closes the modal.
  const deletePhoto = async (photo: Api.PhotoDto) => {
    if (!window.confirm(`Slette «${photo.OriginalFileName}»?`)) return;
    try {
      setDeleting(true);
      await RestService.del(`/api/photos/${photo.Id}`);
      setPhotos(prev => prev.filter(p => p.Id !== photo.Id));
      setLightbox(null);
      toast.success('Bildet ble slettet.');
    } catch (e) {
      toast.error(`Kunne ikke slette: ${e instanceof Error ? e.message : e}`);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <OKomBreadcrumb path='Bilder'></OKomBreadcrumb>
      {isLoading && <OKomLoading text='Laster bilder…' />}

      <div className="ods-grid ods-grid--gap ods-margin-top-5" style={{ padding: '0 1.5rem' }}>
        {/* Left: filters */}
        <div className="ods-grid__column--12 ods-grid__column--3-breakpoint-medium">
          <h2 className="ods-text--size-kilo">Filter</h2>
          {events.length === 0 && <p className="ods-color-text-grayscale-10">Ingen arrangementer.</p>}
          {events.map(e => (
            <OKomCheckbox key={e.key} checked={selectedKeys.includes(e.key)} onChange={c => toggleKey(e.key, c)}>
              {e.label} ({e.count})
            </OKomCheckbox>
          ))}
          {events.length > 0 && (
            <p className="ods-color-text-grayscale-10 ods-margin-top-2" style={{ fontSize: '0.8rem' }}>
              Ingen valgt = alle vises
            </p>
          )}
        </div>

        {/* Right: photo grid */}
        <div className="ods-grid__column--12 ods-grid__column--9-breakpoint-medium">
          {!isLoading && filtered.length === 0 && (
            <p className="ods-color-text-grayscale-10">Ingen bilder å vise ennå.</p>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {filtered.map(p => (
              <div
                key={p.Id}
                style={{
                  width: 160, height: 160, overflow: 'hidden', cursor: 'pointer',
                  border: '1px solid #ddd', borderRadius: 4, background: '#fafafa',
                }}
                onClick={() => setLightbox(p)}
                title={p.OriginalFileName}
              >
                {p.ThumbUrl ? (
                  // Loaded directly from the Azure container (read-only SAS), not via the app.
                  <img
                    src={p.ThumbUrl}
                    alt={p.OriginalFileName}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                ) : p.Status === 'processed' ? (
                  // Fallback (no read SAS configured): proxy through the API.
                  <AuthImage
                    path={`/api/photos/Image/${p.Id}/thumb`}
                    alt={p.OriginalFileName}
                    placeholder={thumbPlaceholder}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                ) : thumbPlaceholder}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Photo detail modal */}
      <OKomModal
        open={lightbox != null}
        onClose={() => setLightbox(null)}
        ariaLabel="Bilde"
        popupStyle={{ width: '80vw', height: '80vh', maxWidth: 'none', position: 'relative', margin: 'auto' }}
      >
        {lightbox && (
          <>
            {/* Prev / next overlay arrows (also work via ←/→ keys) */}
            {currentIndex > 0 && (
              <button
                type="button"
                aria-label="Forrige bilde"
                className="ods-button ods-button--circle ods-button--yellow"
                style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 5 }}
                onClick={() => setLightbox(filtered[currentIndex - 1])}
              >
                <span className="ods-button__icon ods-icon--chevron-left" aria-hidden="true"></span>
              </button>
            )}
            {currentIndex >= 0 && currentIndex < filtered.length - 1 && (
              <button
                type="button"
                aria-label="Neste bilde"
                className="ods-button ods-button--circle ods-button--yellow"
                style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 5 }}
                onClick={() => setLightbox(filtered[currentIndex + 1])}
              >
                <span className="ods-button__icon ods-icon--chevron-right" aria-hidden="true"></span>
              </button>
            )}

          <div className="ods-grid ods-grid--gap">
            {/* Left: the picture */}
            <div className="ods-grid__column--12 ods-grid__column--8-breakpoint-medium">
              {lightbox.MediumUrl ? (
                <img
                  src={lightbox.MediumUrl}
                  alt={lightbox.OriginalFileName}
                  style={{ width: '100%', height: 'auto', maxHeight: '72vh', objectFit: 'contain', display: 'block' }}
                />
              ) : (
                <AuthImage
                  path={`/api/photos/Image/${lightbox.Id}/medium`}
                  alt={lightbox.OriginalFileName}
                  placeholder={<p>Laster…</p>}
                  style={{ width: '100%', height: 'auto', maxHeight: '72vh', objectFit: 'contain', display: 'block' }}
                />
              )}
            </div>

            {/* Right: details */}
            <div className="ods-grid__column--12 ods-grid__column--4-breakpoint-medium">
              <h2 className="ods-text--size-kilo" style={{ wordBreak: 'break-word', marginTop: 0 }}>
                {lightbox.OriginalFileName}
              </h2>

              <div className="ods-margin-top-2" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button className="ods-button" disabled={downloading} onClick={() => downloadRaw(lightbox)}>
                  {downloading ? 'Laster ned…' : 'Last ned'}
                </button>
                {isAdmin && (
                  <button className="ods-button ods-button--red" disabled={deleting} onClick={() => deletePhoto(lightbox)}>
                    {deleting ? 'Sletter…' : 'Slett'}
                  </button>
                )}
              </div>

              <dl className="ods-margin-top-3" style={{ lineHeight: 1.5 }}>
                <dt className="ods-color-text-grayscale-10" style={{ fontSize: '0.8rem' }}>Arrangement</dt>
                <dd style={{ margin: '0 0 0.5rem' }}>{lightbox.EventLabel}</dd>

                <dt className="ods-color-text-grayscale-10" style={{ fontSize: '0.8rem' }}>Opprettet</dt>
                <dd style={{ margin: '0 0 0.5rem' }}>{formatDate(lightbox.DateCreated)}</dd>

                <dt className="ods-color-text-grayscale-10" style={{ fontSize: '0.8rem' }}>Lastet opp av</dt>
                <dd style={{ margin: '0 0 0.5rem', wordBreak: 'break-word' }}>{lightbox.UploadedByEmail || 'Ukjent'}</dd>

                <dt className="ods-color-text-grayscale-10" style={{ fontSize: '0.8rem' }}>Dimensjoner</dt>
                <dd style={{ margin: '0 0 0.5rem' }}>
                  {lightbox.Width && lightbox.Height ? `${lightbox.Width} × ${lightbox.Height} px` : 'Ukjent'}
                </dd>

                <dt className="ods-color-text-grayscale-10" style={{ fontSize: '0.8rem' }}>Filstørrelse</dt>
                <dd style={{ margin: 0 }}>{formatBytes(lightbox.FileSizeBytes)}</dd>
              </dl>
            </div>
          </div>
          </>
        )}
      </OKomModal>
    </>
  );
};

export default withRouter(BilderView);
